import { RefObject, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import CareerSeeker from 'wwg-utilities/dist/collections/career-seeker';
import Program from 'wwg-utilities/dist/collections/program';
import Provider from 'wwg-utilities/dist/collections/provider';
import SeekerConnection from 'wwg-utilities/dist/collections/seeker-connection';
import MyListMessage from '../../components/shared/MyListMessage';

import createTrainingConnectionRequest from '../api/createTrainingConnectionRequest';
import deleteTrainingConnectionRequest from '../api/deleteTrainingConnectionRequest';
import getTrainingConnectionRequest from '../api/getTrainingConnectionRequest';
import { isSkillup } from '../config/project/project';
import {
  addOneHourCookie,
  getCookie,
  PROGRAM_TO_ADD,
  removeCookie,
} from '../cookies';

import { AuthType, ConnectionStatus } from '../enums';
import { dataLayerEvent } from '../gtag';
import { useAuth } from '../providers/authProvider';
import { useAuthType } from '../providers/authTypeState';
import gaDataLayerEvent from './GA/gaDataLayerEvent';

const useTrainingConnection = (
  program: Program,
  seeker: CareerSeeker | null,
  programRef: RefObject<HTMLDivElement | null> | null,
  shouldFadeOnDelete?: boolean,
) => {
  // This holds the state for whether or not the user has a career connection
  const [connection, setConnection] = useState<SeekerConnection | null>(null);
  // This is a custom state provider that allows us to open and close the login/register modal
  const { setAuthType } = useAuthType();
  const { updateSeekerTasks } = useAuth();
  const providerName = useRef(
    (program.institution as Provider)?.name || program.providerName,
  );
  const programAtProvider = useRef(
    `${program.name} at ${
      (program.institution as Provider)?.name || program.providerName
    }`,
  );
  const profileUrl = useRef(`/profile/my-training`);
  // This accesses the API to create a SeekerConnection object in the DB
  const addSeekerConnection = async (
    seeker: CareerSeeker,
    program: Program,
  ) => {
    try {
      const { connection, task } = await createTrainingConnectionRequest(
        seeker.id,
        program.id,
        ConnectionStatus.Saved,
        providerName.current,
        `${seeker.name} <> ${programAtProvider.current}`,
        program.catalog || [],
      );
      if (isSkillup) {
        updateSeekerTasks(task);
      }
      // If it's successful, we're setting the state for the the connection
      setConnection(connection);
      // and we're letting the user know with an alert
      toast(
        MyListMessage({
          message: `${programAtProvider.current} successfully added to`,
          url: profileUrl.current,
        }),
        {
          type: 'success',
        },
      );
      dataLayerEvent('programLiked', {
        key: 'program-name',
        value: `${program.name} at ${
          (program.institution as Provider)?.name || program.providerName
        }`,
      });
      gaDataLayerEvent({
        event: 'item_saved',
        item_type: 'program',
        item_name: `${program.name} at ${
          (program.institution as Provider)?.name || program.providerName
        }`,
      });
    } catch (err) {
      // If it's unsucessful, we're letting the user nkow with an alert
      toast(
        MyListMessage({
          message: `Error adding ${programAtProvider.current} to`,
          url: profileUrl.current,
        }),
        {
          type: 'error',
        },
      );
    }
  };

  // This called the API to remove the SeekerConnection oject from the DB
  const removeSeekerConnection = async (programId: string) => {
    try {
      const { task } = await deleteTrainingConnectionRequest(
        connection?.id as string,
        programId,
      );
      if (isSkillup) {
        updateSeekerTasks(task);
      }

      // If we're successful, clear out the connection
      setConnection(null);
      // and we let the user know it was successful
      toast(
        MyListMessage({
          message: `${programAtProvider.current} successfully removed from`,
          url: profileUrl.current,
        }),
        {
          type: 'success',
        },
      );
      if (shouldFadeOnDelete) {
        programRef?.current?.classList.add('animate-fade-out-2');
        setTimeout(() => {
          programRef?.current?.classList.add('hidden');
        }, 750);
      }
      dataLayerEvent('programDisLiked', {
        key: 'program-name',
        value: `${program.name} at ${
          (program.institution as Provider)?.name || program.providerName
        }`,
      });
    } catch {
      // If it's not successful, we let the user know
      toast(
        MyListMessage({
          message: `Error removing ${programAtProvider.current} from`,
          url: profileUrl.current,
        }),
        {
          type: 'error',
        },
      );
    }
  };
  const handleClick = () => {
    // If the user is not logged in,
    // Show the login form
    if (!seeker) {
      setAuthType(AuthType.Login);
      addOneHourCookie(PROGRAM_TO_ADD, program.id);
      return;
    }

    // If the user is logged in, and
    // there is no connection,
    // add a connection
    if (!connection) {
      addSeekerConnection(seeker as CareerSeeker, program);
    }

    // If the user is logged in, and there is a connection
    // remove the connection
    if (connection) {
      removeSeekerConnection(connection.id);
    }
  };

  // This listens for changes to the career and seeker object
  // to reinitialize the connection. Mostly this is useful for the
  // first render to initially set the connection
  useEffect(() => {
    if (program && seeker) {
      getAndSetConnection(program, seeker);
    } else {
      setConnection(null);
    }
    return () => setConnection(null);
  }, [program?.id, seeker?.id]);

  // This accesses the API to pull the connection
  const getAndSetConnection = async (
    program: Program,
    seeker: CareerSeeker,
  ) => {
    const response = await getTrainingConnectionRequest(seeker?.id, program.id);
    setConnection(response || null);
    const programToAdd = getCookie(PROGRAM_TO_ADD);
    if (programToAdd == program.id) {
      if (!response) {
        await addSeekerConnection(seeker, program);
      }
      removeCookie(programToAdd);
    }
  };

  return {
    connection,
    handleClick,
  };
};

export default useTrainingConnection;
